import React, { useState } from 'react';
import {
  Share2,
  Copy,
  Check,
  Download,
  Hash,
  MessageSquare,
  ImageIcon,
  FileImage,
  Instagram,
  Image as ImageLucide,
  Monitor,
  Mail,
  FileText,
  Facebook,
  Twitter,
  Linkedin,
} from 'lucide-react';
import { socialPosts, hashtagBank, digitalAssets } from '../mock';
import { toast } from 'sonner';

const iconMap = {
  FileImage,
  Instagram,
  Image: ImageLucide,
  Monitor,
  Mail,
  FileText,
};

const TABS = [
  { id: 'posts', label: 'Ready-to-post captions', icon: MessageSquare },
  { id: 'assets', label: 'Downloadable assets', icon: ImageIcon },
  { id: 'hashtags', label: 'Hashtag bank', icon: Hash },
];

const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://acci.org.uk/';
const shareText = 'Take A Stand with ACCI — 38+ years of holistic mental health care for the African & Caribbean community.';

const shareChannels = [
  {
    id: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
  },
  {
    id: 'x',
    label: 'X / Twitter',
    icon: Twitter,
    href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=TakeAStandACCI`,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageSquare,
    href: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
  },
];

const copy = async (text, label = 'Copied') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch (_) {
    toast.error('Could not copy — try manually selecting the text.');
  }
};

const DigitalResources = () => {
  const [tab, setTab] = useState('posts');
  const [copiedId, setCopiedId] = useState(null);

  const handleCopy = (id, text, label) => {
    copy(text, label);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <section
      id="resources"
      data-testid="digital-resources-section"
      className="relative py-24 lg:py-28 bg-[#F7F7F8] overflow-hidden"
    >
      <div className="brush-swash w-72 h-14 top-14 right-0" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-4">
              <Share2 className="w-3.5 h-3.5" />
              Digital Resources
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl font-bold text-[#0E1112] leading-[1.02]">
              Share the <span className="hl-box hl-cyan">stand</span>.<br />
              Ready in one tap.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-neutral-700 text-lg leading-relaxed">
              A social media toolkit for the community. Grab captions, assets and hashtags —
              or share Take A Stand straight to your feed.
            </p>
          </div>
        </div>

        {/* Quick-share bar */}
        <div
          data-testid="quick-share-bar"
          className="rounded-2xl bg-white border border-black/10 p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-10"
        >
          <div className="text-sm font-semibold text-[#0E1112] flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#6e12ed]" />
            Share instantly
          </div>
          <div className="flex flex-wrap gap-2">
            {shareChannels.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.id}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${c.id}`}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-black/10 bg-white text-sm font-semibold text-[#0E1112] hover:border-[#6e12ed] hover:text-[#6e12ed] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                  {c.label}
                </a>
              );
            })}
            <button
              data-testid="share-copy-link"
              onClick={() => handleCopy('link', shareUrl, 'Link copied')}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#0E1112] text-white text-sm font-semibold hover:bg-[#2a2d2e] transition-colors"
            >
              {copiedId === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copy link
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          data-testid="resources-tabs"
          role="tablist"
          className="inline-flex bg-white border border-black/10 rounded-full p-1 mb-8 overflow-x-auto no-scrollbar"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                data-testid={`resources-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-[#6e12ed] text-white'
                    : 'text-neutral-700 hover:text-[#0E1112]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Panel: captions */}
        {tab === 'posts' && (
          <div data-testid="resources-panel-posts" className="grid md:grid-cols-2 gap-5">
            {socialPosts.map((p) => (
              <div
                key={p.id}
                className="rounded-2xl bg-white border border-black/10 p-6 flex flex-col hover:border-[#6e12ed] hover:shadow-md transition-all"
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#6e12ed] mb-2">
                  {p.platform}
                </div>
                <p className="text-sm text-neutral-800 leading-relaxed flex-1">{p.caption}</p>
                <button
                  data-testid={`copy-caption-${p.id}`}
                  onClick={() => handleCopy(p.id, p.caption, 'Caption copied')}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-4 self-start rounded-full bg-[#0E1112] text-white text-xs font-semibold hover:bg-[#2a2d2e] transition-colors"
                >
                  {copiedId === p.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedId === p.id ? 'Copied' : 'Copy caption'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Panel: assets */}
        {tab === 'assets' && (
          <div data-testid="resources-panel-assets" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {digitalAssets.map((a) => {
              const Icon = iconMap[a.icon] || FileImage;
              const color = a.tone === 'aqua' ? '#79c4c7' : '#6e12ed';
              return (
                <div
                  key={a.id}
                  className="group rounded-2xl bg-white border border-black/10 p-6 hover:border-[#6e12ed] hover:shadow-md transition-all"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: color }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="font-serif text-xl font-bold text-[#0E1112] leading-tight">
                    {a.title}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{a.format}</div>
                  <button
                    data-testid={`asset-download-${a.id}`}
                    onClick={() => toast.info('Asset pack coming soon — we\'ll email it when the shop opens.')}
                    className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-black/10 text-xs font-semibold text-[#0E1112] hover:border-[#6e12ed] hover:text-[#6e12ed] transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Panel: hashtags */}
        {tab === 'hashtags' && (
          <div data-testid="resources-panel-hashtags" className="rounded-2xl bg-white border border-black/10 p-8">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div>
                <div className="font-serif text-2xl font-bold text-[#0E1112]">Hashtag bank</div>
                <p className="text-sm text-neutral-600 mt-1">
                  Tap any tag to copy. Copy them all in one go with the button on the right.
                </p>
              </div>
              <button
                data-testid="copy-all-hashtags"
                onClick={() => handleCopy('all-tags', hashtagBank.join(' '), 'All hashtags copied')}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#6e12ed] text-white text-xs font-semibold hover:bg-[#5A0DCC] transition-colors"
              >
                {copiedId === 'all-tags' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                Copy all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {hashtagBank.map((h) => (
                <button
                  key={h}
                  data-testid={`hashtag-${h.replace('#', '')}`}
                  onClick={() => handleCopy(h, h, `${h} copied`)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-black/10 bg-[#F7F7F8] text-sm font-semibold text-[#0E1112] hover:border-[#6e12ed] hover:text-[#6e12ed] hover:bg-white transition-all"
                >
                  {copiedId === h ? <Check className="w-3.5 h-3.5" /> : null}
                  {h}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DigitalResources;
